import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { PerformanceCardProps } from './types'
import { formatVND } from '@/modules/Report/components/TargetAnalytics/helpers'
import { Typogaphy } from 'vnetwork-ui'

export default function PerformanceCard({
    employee,
    onEmployeeClick,
    className,
}: PerformanceCardProps) {
    const { t } = useTranslation('report')

    const isAchieved = employee.status === 'achieved'
    const achievementPercent = employee.achievement_percentage

    const getStatusBadge = () => {
        return (
            <Typogaphy
                className={cn(
                    'h-5 flex items-center px-1 rounded-full text-white text-[10px] truncate',
                    isAchieved ? 'bg-green-500 px-2' : 'bg-red-500'
                )}
            >
                {isAchieved
                    ? t('target_analytics.status.achieved')?.toUpperCase() ||
                      'ACHIEVED'
                    : t(
                          'target_analytics.status.not_achieved'
                      )?.toUpperCase() || 'PENDING'}
            </Typogaphy>
        )
    }

    const getMetricBadge = (actual: number, target: number) => {
        const isTargetMet = actual >= target

        return (
            <Typogaphy
                className={cn(
                    'flex items-center justify-center h-5 gap-0.5',
                    'text-[10px] px-1',
                    isTargetMet
                        ? 'text-blue-950'
                        : 'text-red-500 bg-red-100 rounded-full'
                )}
            >
                <span>{actual}</span>
                <span>/</span>
                <span>{target}</span>
            </Typogaphy>
        )
    }

    return (
        <div
            className={cn(
                'group bg-white rounded-md border border-gray-200 p-3',
                'shadow-sm hover:shadow-lg hover:border-gray-300',
                'transition-all duration-200 cursor-pointer',
                className
            )}
            onClick={() => onEmployeeClick?.(employee)}
        >
            <div className="grid grid-cols-6 gap-3">
                <div className="flex flex-col col-span-3 gap-1.5">
                    {/* Header Section */}
                    <div className="flex flex-col gap-1">
                        <Typogaphy className="text-sm font-medium text-primary leading-tight capitalize">
                            {employee.employee_name}
                        </Typogaphy>
                        <Typogaphy className="text-xs text-gray-500">
                            {employee.department ||
                                t('target_analytics.default_department')}
                        </Typogaphy>
                    </div>

                    {/* Revenue Display Section */}
                    <div className="flex flex-col gap-2">
                        <div className="flex items-baseline gap-3">
                            <span className="text-sm font-semibold text-gray-900 tracking-tight">
                                {formatVND(employee.actual_revenue)}
                            </span>
                        </div>
                        {/* Progress Bar Section */}
                        <div className="space-y-2">
                            <div
                                className={cn(
                                    'w-full bg-gray-100 h-5 overflow-hidden shadow-inner flex items-center',
                                    'bg-blue-50'
                                )}
                            >
                                <div
                                    className={cn(
                                        'h-3 transition-all duration-500 ease-out',
                                        'overflow-hidden',
                                        'bg-green-300'
                                    )}
                                    style={{
                                        width: `${Math.min(achievementPercent, 100)}%`,
                                    }}
                                ></div>
                            </div>
                            <div
                                className={cn(
                                    'flex items-center gap-1.5 mt-2',
                                    'font-medium text-xs'
                                )}
                            >
                                <p>
                                    {t(
                                        'target_analytics.performance_chart.target'
                                    )}
                                    :{' '}
                                    <span>
                                        {formatVND(employee.target_amount)}
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Basic Metric Section */}
                <div className="col-span-3 grid grid-cols-5">
                    <div className="col-span-3">
                        <div className="flex flex-col gap-2 font-medium uppercase text-[10px]">
                            <Typogaphy className="h-5 text-[10px] flex items-center">
                                {t('target_analytics.status.label')}
                            </Typogaphy>
                            <Typogaphy className="h-5 text-[10px] flex items-center">
                                {t('target_analytics.table.presentation')}
                            </Typogaphy>
                            <Typogaphy className="h-5 text-[10px] flex items-center">
                                {t('target_analytics.table.poc')}
                            </Typogaphy>
                            <Typogaphy className="h-5 text-[10px] flex items-center">
                                {t('target_analytics.table.contract')}
                            </Typogaphy>
                        </div>
                    </div>
                    <div className="col-span-2">
                        <div className="flex flex-col items-center gap-2 text-[10px]">
                            {getStatusBadge()}
                            {getMetricBadge(
                                employee.metrics.presentation.actual,
                                employee.metrics.presentation.target
                            )}
                            {getMetricBadge(
                                employee.metrics.poc.actual,
                                employee.metrics.poc.target
                            )}
                            {getMetricBadge(
                                employee.metrics.contract.actual,
                                employee.metrics.contract.target
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
